import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Api, Close, Code, Email, Rule, Storage, Webhook } from '@mui/icons-material';
import { Button, Card, CardContent, Grid, IconButton, Typography } from '@mui/material';
import AddIcon from "@mui/icons-material/Add";

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { BLOCK_TYPE } from '../../utils/constants/block';

// Block categories with descriptions, icons, and colors
const categories = [
  {
    name: 'Decision/Evaluator',
    description: 'Nodes used to evaluate conditions and make decisions in the workflow. The flow forks into true/false paths based on the outcome.',
    icon: <Rule style={{ color: '#1976d2' }} />,
    path: BLOCK_TYPE.Evaluator,
  },
  {
    name: 'Transformer/Custom',
    description: 'Nodes that transform, aggregate, or apply custom logic to data within the workflow.',
    icon: <Code style={{ color: '#ff9800' }} />,
    path: BLOCK_TYPE.Transformer,
  },
  {
    name: 'Data Enricher - API',
    description: 'Fetches additional data by calling external APIs to enrich the workflow data.',
    icon: <Api style={{ color: '#009688' }} />,
    path: BLOCK_TYPE.DataEnricher_API,
  },
  {
    name: 'Data Enricher - DB Lookup',
    description: 'Queries databases to retrieve and enrich data in the workflow.',
    icon: <Storage style={{ color: '#8d6e63' }} />,
    path: BLOCK_TYPE.DataEnricher_DB_Lookup,
  },
  {
    name: 'Data Enricher - Graph Lookup',
    description: 'Performs graph database lookups to retrieve relationships or hierarchies.',
    icon: <Storage style={{ color: '#3f51b5' }} />,
    path: BLOCK_TYPE.DataEnricher_Graph_Lookup,
  },
  {
    name: 'Data Enricher - ML Prediction',
    description: 'Uses machine learning models to make predictions based on input data.',
    icon: <Code style={{ color: '#9c27b0' }} />,
    path: BLOCK_TYPE.DataEnricher_ML_Prediction,
  },
  {
    name: 'Push - Webhook',
    description: 'Sends data to an external service or system via a webhook.',
    icon: <Webhook style={{ color: '#f44336' }} />,
    path: BLOCK_TYPE.Push_Webhook,
  },
  {
    name: 'Push - DB',
    description: 'Inserts or updates data in a database as part of the workflow.',
    icon: <Storage style={{ color: '#2196f3' }} />,
    path: BLOCK_TYPE.Push_DB,
  },
  {
    name: 'Push - Queue',
    description: 'Pushes messages to a queue (e.g., Kafka, RabbitMQ) for asynchronous processing.',
    icon: <Webhook style={{ color: '#ff9800' }} />,
    path: BLOCK_TYPE.Push_Queue,
  },
  {
    name: 'Notification - Email',
    description: 'Sends email notifications based on workflow events.',
    icon: <Email style={{ color: '#4caf50' }} />,
    path: BLOCK_TYPE.Notification_Email,
  },
  {
    name: 'Notification - Slack',
    description: 'Sends Slack notifications to alert about workflow events.',
    icon: <Email style={{ color: '#673ab7' }} />,
    path: BLOCK_TYPE.Notification_Slack,
  }
];

// Reusable SelectableCard component
const SelectableCard = ({ name, description, icon, onClick }: { name: string, description: string, icon: JSX.Element, onClick: () => void }) => (
  <Card
    onClick={onClick}
    sx={{
      flex: 1,
      boxShadow: 'none',
      borderRadius: 2,
      border: '1px solid #e0e0e0',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
      '&:active': {
        animation: 'pulse 1s infinite',
      }
    }}
  >
    <CardContent>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ marginRight: 8 }}>{icon}</div>
        <Typography variant="h6">{name}</Typography>
      </div>
      <Typography variant="body2">{description}</Typography>
    </CardContent>
  </Card>
);

// Dialog to display categories
const CategoryDialog = ({ open, onClose }: { open: boolean, onClose: () => void }) => {
  const navigate = useNavigate(); // Initialize navigate hook

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Select a Block Type
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Flexible Grid for displaying categories */}
        <Grid container spacing={2}>
          {categories.map((category, index) => (
            <Grid item xs={12} sm={6} md={4} lg={4} key={index} sx={{ display: 'flex' }}>
              <SelectableCard
                name={category.name}
                description={category.description}
                icon={category.icon}
                onClick={() => navigate(`/blocks/${category.path}/add`)}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};


// Button to trigger the dialog
const AddBlockButton = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <div>
      <Button variant="contained" onClick={handleOpen} sx={{ mr: 2 }} startIcon={<AddIcon />} >
        Add Block
      </Button>
      <CategoryDialog open={open} onClose={handleClose} />
    </div>
  );
};

export default AddBlockButton;
